import { Injectable } from '@angular/core';

import { ListResponse } from '../../models/list-response';
import {
  EventType,
  EventQuestion,
  EventPricingMatrix,
  EventSchedule,
  NonRepeatingEvent,
  EventTimeSlot,
  EventTypeFilters,
  EventTypeWithPricingMatrices,
  EventScheduleException,
  EventDiscountFilters,
  EventDiscount,
  EventTypeCategory,
  EventTypeLocationSettings, RosterFilters, Roster,
  ScheduleDeletionPreview
} from '../../models/event-type';

import { environment } from '../../../environments/environment';
import { ApiService } from '../api/api.service';
import { propExists } from '../../utils';
import { HttpParams } from '@angular/common/http';
import { WaitlistEntry } from '../../models/scheduled-event';

@Injectable({
  providedIn: 'root'
})
export class EventTypeService {
  /**
   * A service providing access to event types and their constituent
   * subcollections via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of event types from the API.
   */
  getEventTypes(pageNumber?: number,
                pageSize?: number,
                filters?: EventTypeFilters): Promise<ListResponse<EventType>> {
    return this.apiService.getCollection<EventType, EventTypeFilters>(
      'event-types/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get an individual event type from the API.
   */
  getEventType(id: number): Promise<EventType> {
    return this.apiService.getItem<EventType>(
      'event-types/',
      id
    );
  }

  /**
   * Post a new event type to the API.
   *
   * @param eventType - The new EventType object to create.
   * @return The new event type returned from the API.
   */
  addEventType(eventType: EventType): Promise<EventType> {
    return this.apiService.postToAPI<EventType>(
      'event-types/',
      {
        schedule_type: eventType.schedule_type,
        name: eventType.name,
        description_text: eventType.description_text,
        status: eventType.status,
        reserves_area_capacity: eventType.reserves_area_capacity,
        late_booking_window_minutes: eventType.late_booking_window_minutes,
        is_listed: eventType.is_listed,
        has_automatic_check_in: eventType.has_automatic_check_in,
        max_reservations_per_day_for_customer: eventType.max_reservations_per_day_for_customer,
        min_participant_age: eventType.min_participant_age,
        max_participant_age: eventType.max_participant_age,
        calendar_color_hex: eventType.calendar_color_hex,
        require_login_for_booking: eventType.require_login_for_booking,
        tax_rate_id: eventType.tax_rate_id,
        max_participant_count: eventType.max_participant_count,
        max_bookings_per_event: eventType.max_bookings_per_event,
        display_pricing_in_climber_app_listings: eventType.display_pricing_in_climber_app_listings,
        display_remaining_capacity_in_climber_app_listings: eventType.display_remaining_capacity_in_climber_app_listings,
        participant_agreement_documents: eventType.participant_agreement_documents,
        required_waiver_documents: eventType.required_waiver_documents,
        mailchimp_check_in_participant_event_name: eventType.mailchimp_check_in_participant_event_name,
        mailchimp_check_in_booking_customer_event_name: eventType.mailchimp_check_in_booking_customer_event_name,
        mailchimp_completed_participant_event_name: eventType.mailchimp_completed_participant_event_name,
        mailchimp_completed_booking_customer_event_name: eventType.mailchimp_completed_booking_customer_event_name,
        is_private_event_request_form: eventType.is_private_event_request_form,
        has_limited_location_availability: eventType.has_limited_location_availability,
        allowed_location_ids: eventType.allowed_location_ids,
      }
    );
  }

  /**
   * Update an event type's status by making a PATCH request to the API.
   *
   * @param eventType - The event type, with updated status.
   * @return The updated event type returned from the API.
   */
  updateEventTypeStatus(eventType: EventType): Promise<EventType> {
    return this.apiService.patchToAPI<EventType>(
      eventType.url,
      {
        status: eventType.status
      }
    );
  }

  /**
   * Update an event type's basic details by making a PATCH request to the API.
   *
   * @param eventType - The event type, with updated values.
   * @return The updated event type returned from the API.
   */
  updateEventTypeDetails(eventType: EventType): Promise<EventType> {
    return this.apiService.patchToAPI<EventType>(
      eventType.url,
      {
        schedule_type: eventType.schedule_type,
        name: eventType.name,
        description_text: eventType.description_text,
        reserves_area_capacity: eventType.reserves_area_capacity,
        late_booking_window_minutes: eventType.late_booking_window_minutes,
        is_listed: eventType.is_listed,
        has_automatic_check_in: eventType.has_automatic_check_in,
        max_reservations_per_day_for_customer: eventType.max_reservations_per_day_for_customer,
        min_participant_age: eventType.min_participant_age,
        max_participant_age: eventType.max_participant_age,
        calendar_color_hex: eventType.calendar_color_hex,
        require_login_for_booking: eventType.require_login_for_booking,
        tax_rate_id: eventType.tax_rate_id,
        max_participant_count: eventType.max_participant_count,
        max_bookings_per_event: eventType.max_bookings_per_event,
        display_pricing_in_climber_app_listings: eventType.display_pricing_in_climber_app_listings,
        display_remaining_capacity_in_climber_app_listings: eventType.display_remaining_capacity_in_climber_app_listings,
        participant_agreement_documents: eventType.participant_agreement_documents,
        required_waiver_documents: eventType.required_waiver_documents,
        mailchimp_check_in_participant_event_name: eventType.mailchimp_check_in_participant_event_name,
        mailchimp_check_in_booking_customer_event_name: eventType.mailchimp_check_in_booking_customer_event_name,
        mailchimp_completed_participant_event_name: eventType.mailchimp_completed_participant_event_name,
        mailchimp_completed_booking_customer_event_name: eventType.mailchimp_completed_booking_customer_event_name,
        is_private_event_request_form: eventType.is_private_event_request_form,
        has_limited_location_availability: eventType.has_limited_location_availability,
        allowed_location_ids: eventType.allowed_location_ids,
      }
    );
  }

  /**
   * Update an event type's customer cancellation policy by making a PATCH
   * request to the API.
   *
   * @param eventType - The event type, with updated values.
   * @return The updated event type returned from the API.
   */
  updateEventTypeCancellationPolicy(eventType: EventType): Promise<EventType> {
    return this.apiService.patchToAPI<EventType>(
      eventType.url,
      {
        allow_customer_cancellation: eventType.allow_customer_cancellation,
        customer_cancellation_window_hours: eventType.customer_cancellation_window_hours,
        customer_cancellation_currency_refund_amount: eventType.customer_cancellation_currency_refund_amount,
        customer_cancellation_currency_refund_as_credit: eventType.customer_cancellation_currency_refund_as_credit,
        refund_voucher_on_customer_cancellation: eventType.refund_voucher_on_customer_cancellation,
      }
    );
  }

  /**
   * Update an event type's waitlist settings by making a PATCH
   * request to the API.
   *
   * @param eventType - The event type, with updated values.
   * @return The updated event type returned from the API.
   */
  updateEventTypeWaitlistSettings(eventType: EventType): Promise<EventType> {
    return this.apiService.patchToAPI<EventType>(
      eventType.url,
      {
        waitlist_scope: eventType.waitlist_scope,
        allow_customers_to_join_waitlist: eventType.allow_customers_to_join_waitlist,
        display_waitlist_count_in_climber_app: eventType.display_waitlist_count_in_climber_app,
        automatically_invite_off_waitlist: eventType.automatically_invite_off_waitlist,
        min_hours_before_event_for_automatic_waitlist_invite: eventType.min_hours_before_event_for_automatic_waitlist_invite,
        hours_to_accept_waitlist_spot: eventType.hours_to_accept_waitlist_spot,
        allow_waitlist_customers_to_select_preferred_days_of_week: eventType.allow_waitlist_customers_to_select_preferred_days_of_week,
        allow_waitlist_customers_to_select_preferred_locations: eventType.allow_waitlist_customers_to_select_preferred_locations,
        require_payment_method_to_join_waitlist: eventType.require_payment_method_to_join_waitlist,
        require_membership_ts_and_cs_to_join_waitlist: eventType.require_membership_ts_and_cs_to_join_waitlist,
        waitlist_staff_notification_email_addresses: eventType.waitlist_staff_notification_email_addresses,
        waitlist_confirmation_email_text: eventType.waitlist_confirmation_email_text,
      }
    );
  }

  /**
   * Get a preview of the "added to waitlist" confirmation email for the given
   * event type. If `waitlistConfirmationEmailText` is supplied, it overrides
   * the event type's currently-saved value (so unsaved input can be previewed).
   */
  previewWaitlistConfirmationEmail(eventTypeId: number,
                                   waitlistConfirmationEmailText: string): Promise<{
    subject: string;
    text_content: string;
    html_content: string;
  }> {
    return this.apiService.postToAPI(
      'gym-endpoints/preview-waitlist-confirmation-email/' + eventTypeId.toString() + '/',
      {
        waitlist_confirmation_email_text: waitlistConfirmationEmailText
      }
    );
  }

  /**
   * Update an event type's Scheduled Events columns by making a PATCH
   * request to the API.
   *
   * @param eventType - The event type, with updated values.
   * @return The updated event type returned from the API.
   */
  updateEventTypeScheduledEventsColumns(eventType: EventType): Promise<EventType> {
    return this.apiService.patchToAPI<EventType>(
      eventType.url,
      {
        scheduled_events_columns: eventType.scheduled_events_columns
      }
    );
  }

  /**
   * Make a copy of an event type, including it's related configuration objects
   * (such as location settings and participant questions), but not including
   * occurrences and schedules.
   *
   * @return The ID of the newly-created event type.
   */
  duplicateEventType(eventTypeId: number, name: string): Promise<number> {
    return this.apiService.postToAPI<number>(
      'event-types/' + eventTypeId.toString() + '/duplicate/',
      {
        name
      }
    );
  }

  /**
   * Get a page of event type location settings from the API.
   */
  getEventTypeLocationSettings(eventTypeId: number,
                               pageNumber?: number,
                               pageSize?: number): Promise<ListResponse<EventTypeLocationSettings>> {
    return this.apiService.getCollectionNoFilters<EventTypeLocationSettings>(
      'event-types/' + eventTypeId.toString() + '/location-settings/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Update an event type's location settings for a particular location by
   * making a PATCH request to the API.
   *
   * @param etLocSettings - The event type location settings, with updated values.
   * @return The updated event type returned from the API.
   */
  updateEventTypeLocationSettings(etLocSettings: EventTypeLocationSettings): Promise<EventTypeLocationSettings> {
    return this.apiService.patchToAPI<EventTypeLocationSettings>(
      etLocSettings.url,
      {
        confirmation_text: etLocSettings.confirmation_text,
        payment_information_text: etLocSettings.payment_information_text,
        send_post_event_email: etLocSettings.send_post_event_email,
        post_event_email_text: etLocSettings.post_event_email_text,
        post_event_email_delay_hours: etLocSettings.post_event_email_delay_hours,
        post_event_email_attendees_only: etLocSettings.post_event_email_attendees_only,
        send_reminder_email: etLocSettings.send_reminder_email,
        reminder_email_text: etLocSettings.reminder_email_text,
        reminder_email_lead_hours: etLocSettings.reminder_email_lead_hours,
        send_waiver_email: etLocSettings.send_waiver_email,
        waiver_email_text: etLocSettings.waiver_email_text,
        waiver_email_lead_hours: etLocSettings.waiver_email_lead_hours,
        waiver_email_document_template_id: etLocSettings.waiver_email_document_template_id,
        waiver_email_document_template_name: etLocSettings.waiver_email_document_template_name,
        staff_notification_email_addresses: etLocSettings.staff_notification_email_addresses
      }
    );
  }

  /**
   * Get a page of event pricing matrices for the given event type from the API.
   */
  getEventPricingMatrices(eventTypeId: number,
                          pageNumber?: number,
                          pageSize?: number): Promise<ListResponse<EventPricingMatrix>> {
    return this.apiService.getCollectionNoFilters<EventPricingMatrix>(
      'event-types/' + eventTypeId.toString() + '/pricing-matrices/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get an event pricing matrix from the API.
   */
  getEventPricingMatrix(eventTypeId: number,
                        matrixId: number): Promise<EventPricingMatrix> {
    return this.apiService.getItem<EventPricingMatrix>(
      'event-types/' + eventTypeId.toString() + '/pricing-matrices/',
      matrixId
    );
  }

  /**
   * Post a new event pricing matrix to the API.
   *
   * @param eventTypeId - The ID of the event type to which the matrix belongs.
   * @param pricingMatrix - The new matrix object to create.
   * @return The new matrix returned from the API.
   */
  addEventPricingMatrix(eventTypeId: number,
                        pricingMatrix: EventPricingMatrix): Promise<EventPricingMatrix> {
    // Make all fee values strings before saving.
    makeAllFeeValuesStrings(pricingMatrix);

    return this.apiService.postToAPI<EventPricingMatrix>(
      'event-types/' + eventTypeId.toString() + '/pricing-matrices/',
      pricingMatrix
    );
  }

  /**
   * Update an event pricing matrix by making a PUT request to the API.
   *
   * @param pricingMatrix - The matrix, with updated values.
   * @return The updated matrix returned from the API.
   */
  updateEventPricingMatrix(pricingMatrix: EventPricingMatrix): Promise<EventPricingMatrix> {
    // Make all fee values strings before saving.
    makeAllFeeValuesStrings(pricingMatrix);

    return this.apiService.putToAPI<EventPricingMatrix>(
      pricingMatrix.url,
      pricingMatrix
    );
  }

  /**
   * Remove an event pricing matrix by making a PATCH request to the API,
   * setting its is_removed value to true.
   *
   * @param pricingMatrix - The matrix to remove.
   * @return The updated matrix returned from the API.
   */
  removeEventPricingMatrix(pricingMatrix: EventPricingMatrix): Promise<EventPricingMatrix> {
    return this.apiService.patchToAPI<EventPricingMatrix>(
      pricingMatrix.url,
      {is_removed: true}
    );
  }

  /**
   * Obtain all of the event pricing matrices belonging to active event types
   * for the user's organization, grouped by event type. This endpoint removes
   * the need to make separate queries per event type when loading the
   * Accounting Configuration Staff Site page.
   */
  getAllActiveEventPricingMatrices(): Promise<EventTypeWithPricingMatrices[]> {
    return this.apiService.getFromAPI<EventTypeWithPricingMatrices[]>(
      'gym-endpoints/get-active-event-pricing-matrices/'
    );
  }

  /**
   * Obtain all of the event pricing matrices belonging to event types
   * for the user's organization, grouped by event type. This endpoint removes
   * the need to make separate queries per event type when loading the
   * Event Discount Staff Site page.
   */
  getAllEventPricingMatrices(): Promise<EventTypeWithPricingMatrices[]> {
    return this.apiService.getFromAPI<EventTypeWithPricingMatrices[]>(
      'gym-endpoints/get-all-event-pricing-matrices/'
    );
  }

  /**
   * Get a page of event schedules for the given event type from the API.
   *
   * Only relevant for repeating and rolling event types.
   */
  getEventSchedules(eventTypeId: number,
                    pageNumber?: number,
                    pageSize?: number): Promise<ListResponse<EventSchedule>> {
    return this.apiService.getCollectionNoFilters<EventSchedule>(
      'event-types/' + eventTypeId.toString() + '/schedules/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get an event schedule from the API.
   *
   * Only relevant for repeating and rolling event types.
   */
  getEventSchedule(eventTypeId: number,
                   scheduleId: number): Promise<EventSchedule> {
    return this.apiService.getItem<EventSchedule>(
      'event-types/' + eventTypeId.toString() + '/schedules/',
      scheduleId
    );
  }

  /**
   * Post a new event schedule to the API.
   *
   * Only relevant for repeating and rolling event types.
   *
   * @param eventTypeId - The ID of the event type to which the schedule belongs.
   * @param eventSchedule - The new event schedule object to create.
   * @return The new event schedule returned from the API.
   */
  addEventSchedule(eventTypeId: number,
                   eventSchedule: EventSchedule): Promise<EventSchedule> {
    return this.apiService.postToAPI<EventSchedule>(
      'event-types/' + eventTypeId.toString() + '/schedules/',
      eventSchedule
    );
  }

  /**
   * Update an event schedule by making a PUT request to the API.
   *
   * Only relevant for repeating and rolling event types.
   *
   * @param eventSchedule - The event schedule, with updated values.
   * @return The updated event schedule returned from the API.
   */
  updateEventSchedule(eventSchedule: EventSchedule): Promise<EventSchedule> {
    // The html form sets end_date to undefined, but it must be null in order
    // to clear an existing end_date.
    if (!eventSchedule.end_date) {
      eventSchedule.end_date = null;
    }

    return this.apiService.putToAPI<EventSchedule>(
      eventSchedule.url,
      eventSchedule
    );
  }

  /**
   * Delete the given event schedule.
   *
   * Only relevant for repeating and rolling event types.
   */
  deleteEventSchedule(eventSchedule: EventSchedule): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      eventSchedule.url
    ).then();
  }

  /**
   * Get a preview of the events which would be left orphaned if the given
   * schedule were deleted (future events with active reservations).
   *
   * Only relevant for repeating event types; rolling schedules never leave
   * orphaned events.
   */
  getEventScheduleDeletionPreview(
    eventTypeId: number,
    eventScheduleId: number
  ): Promise<ScheduleDeletionPreview> {
    return this.apiService.getFromAPI<ScheduleDeletionPreview>(
      'event-types/' + eventTypeId.toString() +
      '/schedules/' + eventScheduleId.toString() +
      '/deletion-preview/'
    );
  }

  /**
   * Get the list of events for the given event type from the API.
   *
   * Only relevant for one-off event types.
   *
   * null locationId indicates "all locations".
   */
  getNonRepeatingEvents(eventTypeId: number,
                        locationId: number | null,
                        hidePastEvents: boolean): Promise<NonRepeatingEvent[]> {
    let params = new HttpParams();

    if (locationId !== null) {
      params = params.set('location_id', locationId.toString());
    }
    if (hidePastEvents) {
      params = params.set('hide_past_events', 'true');
    }

    return this.apiService.getFromAPI<NonRepeatingEvent[]>(
      'gym-endpoints/get-non-repeating-events/' + eventTypeId + '/',
      params
    );
  }

  getNonRepeatingEvent(eventId: number): Promise<NonRepeatingEvent> {
    return this.apiService.getItem<NonRepeatingEvent>(
      'gym-endpoints/get-non-repeating-event/',
      eventId
    );
  }

  /**
   * Create a new event for the given event type via the API.
   *
   * Only relevant for one-off event types.
   */
  addNonRepeatingEvent(eventTypeId: number,
                       event: NonRepeatingEvent,
                       requestBookingId: number | null): Promise<NonRepeatingEvent> {
    const body: any = {
      location_id: event.location_id,
      location_area_id: event.location_area_id,
      capacity: event.capacity,
      rolling_days: event.rolling_days,
      rolling_days_use_exact_date: event.rolling_days_use_exact_date,
      booking_open_time: event.booking_open_time,
      pricing_matrix_id: event.pricing_matrix_id,
      tax_rate_id: event.tax_rate_id,
      event_parts: event.event_parts.map(eventPart => {
        return {
          start_time: eventPart.start_time,
          start_date: eventPart.start_date,
          duration_minutes: eventPart.duration_minutes,
          instructors: eventPart.instructors.map((i) => {
            return {
              id: i.id,
              is_sub: i.is_sub
            };
          })
        };
      }),
      is_private: event.is_private,
      request_form_booking_id: event.request_form_booking_id,
      private_notes: event.private_notes,
      occurrence_name: event.occurrence_name,
      customer_facing_name: event.customer_facing_name,
      benefit_membership_id: event.benefit_membership_id,
      benefit_membership_additional_days: event.benefit_membership_additional_days,
    };

    if (requestBookingId !== null) {
      body.request_booking_id = requestBookingId;
    }

    return this.apiService.postToAPI<NonRepeatingEvent>(
      'gym-endpoints/create-non-repeating-event/' + eventTypeId + '/',
      body
    );
  }

  /**
   * Update an existing event for the given event type via the API.
   *
   * Only relevant for one-off event types.
   */
  updateNonRepeatingEvent(event: NonRepeatingEvent): Promise<NonRepeatingEvent> {
    const body: any = {
      location_area_id: event.location_area_id,
      capacity: event.capacity,
      rolling_days: event.rolling_days,
      rolling_days_use_exact_date: event.rolling_days_use_exact_date,
      booking_open_time: event.booking_open_time,
      pricing_matrix_id: event.pricing_matrix_id,
      tax_rate_id: event.tax_rate_id,
      event_parts: event.event_parts.map(eventPart => {
        return {
          start_time: eventPart.start_time,
          start_date: eventPart.start_date,
          duration_minutes: eventPart.duration_minutes,
          instructors: eventPart.instructors.map((i) => {
            return {
              id: i.id,
              is_sub: i.is_sub
            };
          })
        };
      }),
      private_notes: event.private_notes,
      occurrence_name: event.occurrence_name,
      customer_facing_name: event.customer_facing_name,
      benefit_membership_id: event.benefit_membership_id,
      benefit_membership_additional_days: event.benefit_membership_additional_days,
    };

    return this.apiService.putToAPI<NonRepeatingEvent>(
      environment.api_url + 'gym-endpoints/update-non-repeating-event/' + event.id + '/',
      body
    );
  }

  /**
   * Get a page of event time slots for the given event schedule from the API.
   */
  getEventTimeSlots(eventTypeId: number,
                    eventScheduleId: number,
                    pageNumber?: number,
                    pageSize?: number): Promise<ListResponse<EventTimeSlot>> {
    return this.apiService.getCollectionNoFilters<EventTimeSlot>(
      'event-types/' + eventTypeId.toString() + '/schedules/' + eventScheduleId.toString() + '/time-slots/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Delete the given event time slot.
   */
  deleteEventTimeSlot(eventTimeSlot: EventTimeSlot): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      eventTimeSlot.url
    ).then();
  }

  /**
   * Post a new event time slot to the API.
   *
   * @param eventTypeId - The ID of the event type to which the time slot belongs.
   * @param eventScheduleId - The ID of the event schedule to which the time slot belongs.
   * @param eventTimeSlot - The new event time slot object to create.
   * @return The new event time slot returned from the API.
   */
  addEventTimeSlot(eventTypeId: number,
                   eventScheduleId: number,
                   eventTimeSlot: EventTimeSlot): Promise<EventTimeSlot> {
    return this.apiService.postToAPI<EventTimeSlot>(
      'event-types/' + eventTypeId.toString() + '/schedules/' + eventScheduleId.toString() + '/time-slots/',
      eventTimeSlot
    );
  }

  /**
   * Update an event time slot by making a PUT request to the API.
   *
   * @param eventTimeSlot - The event time slot, with updated values.
   * @return The updated event time slot returned from the API.
   */
  updateEventTimeSlot(eventTimeSlot: EventTimeSlot): Promise<EventTimeSlot> {
    return this.apiService.putToAPI<EventTimeSlot>(
      eventTimeSlot.url,
      eventTimeSlot
    );
  }

  /**
   * Get a page of event schedule exceptions for the given event type from the API.
   *
   * Only relevant for repeating and rolling event types.
   */
  getEventScheduleExceptions(eventTypeId: number,
                             pageNumber?: number,
                             pageSize?: number): Promise<ListResponse<EventScheduleException>> {
    return this.apiService.getCollectionNoFilters<EventScheduleException>(
      'event-types/' + eventTypeId.toString() + '/schedule-exceptions/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get an event schedule exception from the API.
   *
   * Only relevant for repeating and rolling event types.
   */
  getEventScheduleException(eventTypeId: number,
                            scheduleExceptionId: number): Promise<EventScheduleException> {
    return this.apiService.getItem<EventScheduleException>(
      'event-types/' + eventTypeId.toString() + '/schedule-exceptions/',
      scheduleExceptionId
    );
  }

  /**
   * Post a new event schedule exception to the API.
   *
   * The API will also sync the exception's event type's events as part of this
   * operation.
   *
   * Only relevant for repeating and rolling event types.
   */
  addEventScheduleException(eventTypeId: number,
                            scheduleException: EventScheduleException): Promise<EventScheduleException> {
    return this.apiService.postToAPI<EventScheduleException>(
      'event-types/' + eventTypeId.toString() + '/schedule-exceptions/',
      scheduleException
    );
  }

  /**
   * Update an event schedule exception by making a PUT request to the API.
   *
   * The API will also sync the exception's event type's events as part of this
   * operation.
   *
   * Only relevant for repeating and rolling event types.
   */
  updateEventScheduleException(scheduleException: EventScheduleException): Promise<EventScheduleException> {
    return this.apiService.putToAPI<EventScheduleException>(
      scheduleException.url,
      scheduleException
    );
  }

  /**
   * Delete the given event schedule exception.
   *
   * The API will also sync the exception's event type's events as part of this
   * operation.
   *
   * Only relevant for repeating and rolling event types.
   */
  deleteEventScheduleException(scheduleException: EventScheduleException): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      scheduleException.url
    ).then();
  }

  /**
   * Get a preview of the events which would be left orphaned if the given
   * schedule exception were deleted (future events with active reservations).
   */
  getEventScheduleExceptionDeletionPreview(
    eventTypeId: number,
    scheduleExceptionId: number
  ): Promise<ScheduleDeletionPreview> {
    return this.apiService.getFromAPI<ScheduleDeletionPreview>(
      'event-types/' + eventTypeId.toString() +
      '/schedule-exceptions/' + scheduleExceptionId.toString() +
      '/deletion-preview/'
    );
  }

  /**
   * Get a page of event questions for the given event type from the API.
   */
  getEventQuestions(eventTypeId: number,
                    pageNumber?: number,
                    pageSize?: number): Promise<ListResponse<EventQuestion>> {
    return this.apiService.getCollectionNoFilters<EventQuestion>(
      'event-types/' + eventTypeId.toString() + '/questions/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Post a new event question to the API.
   *
   * @param eventTypeId - The ID of the event type to which the question belongs.
   * @param eventQuestion - The new event question object to create.
   * @return The new event question returned from the API.
   */
  addEventQuestion(eventTypeId: number,
                   eventQuestion: EventQuestion): Promise<EventQuestion> {
    return this.apiService.postToAPI<EventQuestion>(
      'event-types/' + eventTypeId.toString() + '/questions/',
      eventQuestion
    );
  }

  /**
   * Update an event question by making a PUT request to the API.
   *
   * @param eventQuestion - The event question, with updated values.
   * @return The updated event question returned from the API.
   */
  updateEventQuestion(eventQuestion: EventQuestion): Promise<EventQuestion> {
    return this.apiService.putToAPI<EventQuestion>(
      eventQuestion.url,
      eventQuestion
    );
  }

  /**
   * Update the order number of an event question by making a PATCH request to
   * the API.
   *
   * @param eventQuestion - The event question, with an updated order number.
   * @return The updated event question returned from the API.
   */
  setEventQuestionOrderNumber(eventQuestion: EventQuestion): Promise<EventQuestion> {
    return this.apiService.patchToAPI<EventQuestion>(
      eventQuestion.url,
      {order_number: eventQuestion.order_number}
    );
  }

  /**
   * Delete the given event question.
   */
  deleteEventQuestion(eventQuestion: EventQuestion): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      eventQuestion.url
    ).then();
  }

  /**
   * Synchronize the future events associated with the given schedule.
   *
   * @param scheduleId - The ID of the schedule for which to sync events.
   */
  syncScheduleEvents(scheduleId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/sync-schedule-events/',
      {schedule_id: scheduleId}
    ).then();
  }

  /**
   * Get the list of blackout dates for the given location from the API.
   */
  getBlackoutDates(locationId: number): Promise<string[]> {
    return this.apiService.getFromAPI<string[]>(
      'gym-endpoints/blackout-dates/' + locationId.toString() + '/list/'
    );
  }

  /**
   * Add the given date to the given location's list of blackout dates.
   */
  addBlackoutDate(locationId: number, iso8601date: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/blackout-dates/' + locationId.toString() + '/add/' + iso8601date + '/',
      {}
    ).then();
  }

  /**
   * Remove the given date from the given location's list of blackout dates.
   */
  removeBlackoutDate(locationId: number, iso8601date: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/blackout-dates/' + locationId.toString() + '/remove/' + iso8601date + '/',
      {}
    ).then();
  }

  getEventDiscounts(pageNumber?: number,
                    pageSize?: number,
                    filters?: EventDiscountFilters): Promise<ListResponse<EventDiscount>> {
    return this.apiService.getCollection<EventDiscount, EventDiscountFilters>(
      'event-discounts/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getEventDiscount(id: number): Promise<EventDiscount> {
    return this.apiService.getItem<EventDiscount>(
      'event-discounts/',
      id
    );
  }

  addEventDiscount(discount: EventDiscount): Promise<EventDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.postToAPI<EventDiscount>(
      'event-discounts/',
      discount
    );
  }

  updateEventDiscount(discount: EventDiscount): Promise<EventDiscount> {
    if (discount.start_date === '') { discount.start_date = null; }
    if (discount.end_date === '') { discount.end_date = null; }

    return this.apiService.patchToAPI<EventDiscount>(
      discount.url,
      discount
    );
  }

  deleteEventDiscount(discount: EventDiscount): Promise<EventDiscount> {
    return this.apiService.deleteFromAPI<EventDiscount>(
      discount.url
    );
  }

  /**
   * Get a page of event type categories from the API.
   *
   * @param status - Optionally restrict to a status. Omitted returns active
   *     categories; 'removed' returns soft-deleted categories (PRO-1310).
   */
  getEventTypeCategories(pageNumber?: number,
                         pageSize?: number,
                         status?: string): Promise<ListResponse<EventTypeCategory>> {
    return this.apiService.getCollection<EventTypeCategory, {status?: string}>(
      'event-type-categories/',
      pageNumber,
      pageSize,
      status ? {status} : undefined
    );
  }

  /**
   * Restore a previously removed (soft-deleted) event type category (PRO-1310),
   * making it active and visible again in the Staff Site and Climber App.
   */
  restoreEventTypeCategory(id: number): Promise<EventTypeCategory> {
    return this.apiService.postToAPI<EventTypeCategory>(
      'event-type-categories/' + id + '/restore/',
      {}
    );
  }

  getEventTypeCategory(id: number): Promise<EventTypeCategory> {
    return this.apiService.getItem<EventTypeCategory>(
      'event-type-categories/',
      id
    );
  }

  addEventTypeCategory(category: EventTypeCategory): Promise<EventTypeCategory> {
    return this.apiService.postToAPI<EventTypeCategory>(
      'event-type-categories/',
      category
    );
  }

  updateEventTypeCategory(category: EventTypeCategory): Promise<EventTypeCategory> {
    return this.apiService.patchToAPI<EventTypeCategory>(
      category.url,
      category
    );
  }

  deleteEventTypeCategory(category: EventTypeCategory): Promise<EventTypeCategory> {
    return this.apiService.deleteFromAPI<EventTypeCategory>(
      category.url
    );
  }

  setEventTypeCategoriesOrderNumbers(categories: EventTypeCategory[]): Promise<void> {
    const body = {};

    for (const category of categories) {
      body[category.id.toString()] = category.order_number;
    }

    return this.apiService.putToAPI<any>(
      environment.api_url + 'event-type-categories-order-numbers/',
      body
    ).then();
  }

  getEventTypeWaitlistCounts(eventTypeIds: number[]): Promise<{[key: number]: number}> {
    return this.apiService.getFromAPI<{[key: number]: number}>(
      'get-event-type-waitlist-counts/' + eventTypeIds.join(',') + '/'
    );
  }

  getEventTypeWaitlistEntries(eventTypeId: number,
                              pageNumber?: number,
                              pageSize?: number,
                              search?: string,
                              isActive?: boolean,
                              sortByLastActivity?: boolean,
                              participantCustomerId?: number,
                              rosterId?: number): Promise<ListResponse<WaitlistEntry>> {
    const filters: any = {};
    if (search) {
      filters.search = search;
    }
    if (isActive === true) {
      filters.active = true;
    } else if (isActive === false) {
      filters.active = false;
    }
    if (sortByLastActivity === true) {
      filters.ordering = '-updated_at';
    }
    if (participantCustomerId) {
      filters.participant_customer = participantCustomerId;
    }
    if (rosterId) {
      filters.roster = rosterId;
    }

    return this.apiService.getCollection<WaitlistEntry, any>(
      'event-types/' + eventTypeId.toString() + '/waitlist-entries/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getEventTypeWaitlistEntry(eventTypeId: number,
                            waitlistEntryId: number): Promise<WaitlistEntry> {
    return this.apiService.getItem<WaitlistEntry>(
      'event-types/' + eventTypeId.toString() + '/waitlist-entries/',
      waitlistEntryId
    );
  }

  getRosters(pageNumber?: number,
             pageSize?: number,
             filters?: RosterFilters): Promise<ListResponse<Roster>> {
    return this.apiService.getCollection<Roster, RosterFilters>(
      'rosters/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getRoster(id: number): Promise<Roster> {
    return this.apiService.getItem<Roster>(
      'rosters/',
      id
    );
  }

  addRoster(roster: Roster): Promise<Roster> {
    return this.apiService.postToAPI<Roster>(
      'rosters/',
      roster
    );
  }

  updateRoster(roster: Roster): Promise<Roster> {
    return this.apiService.patchToAPI<Roster>(
      roster.url,
      roster
    );
  }

  removeRoster(roster: Roster): Promise<void> {
    return this.apiService.deleteFromAPI<EventPricingMatrix>(
      roster.url
    ).then();
  }

}


function makeAllFeeValuesStrings(pricingMatrix: EventPricingMatrix) {
  /**
   * Convert all fee values in the given pricing matrix to strings, editing them
   * in place. This is necessary because the API requires decimal values to be
   * represented as strings (JSON numbers cannot be interpreted as
   * fixed-precision).
   */
  for (const feeItem of pricingMatrix.per_participant_fees) {
    if (propExists(feeItem.fee)) {
      feeItem.fee = feeItem.fee.toString();
    }
  }
  for (const feeItem of pricingMatrix.per_booking_fees) {
    if (propExists(feeItem.fee)) {
      feeItem.fee = feeItem.fee.toString();
    }
    if (propExists(feeItem.additional_participant_fee)) {
      feeItem.additional_participant_fee = feeItem.additional_participant_fee.toString();
    }
  }
  for (const addOnItem of pricingMatrix.per_participant_add_ons) {
    if (propExists(addOnItem.fee)) {
      addOnItem.fee = addOnItem.fee.toString();
    }
  }
  for (const addOnItem of pricingMatrix.per_booking_add_ons) {
    if (propExists(addOnItem.fee)) {
      addOnItem.fee = addOnItem.fee.toString();
    }
    if (propExists(addOnItem.additional_participant_fee)) {
      addOnItem.additional_participant_fee = addOnItem.additional_participant_fee.toString();
    }
  }

  if (propExists(pricingMatrix.per_booking_deposit)) {
    pricingMatrix.per_booking_deposit = pricingMatrix.per_booking_deposit.toString();
  } else {
    pricingMatrix.per_booking_deposit = null;
  }
}
